<template>
  <div>
    <v-item-group v-model="groups.selected" v-if="groups.list.length > 0">
      <v-row>
        <v-col v-for="group in groups.list"
               :key="group.id"
               cols="4">
          <v-item v-slot="{ active, toggle }"
                  :value="group.id">
            <v-card flat
                    :color="cardColor(group)"
                    @click="toggle" 
                    :dark="!!groups.selected && groups.selected == group.id"
                    >
              <v-toolbar flat
                         dense
                         color="transparent">
                <v-toolbar-title class="text-body-1" style="letter-spacing:0 !important;">
                  {{ group.name }}
                </v-toolbar-title>
                <v-spacer></v-spacer>
                <v-badge inline :color="group.id == groups.selected ? 'grey darken-4' : 'grey darken-1'" :content="group.count || '0'" />
                <v-menu left offset-y bottom :close-on-content-click="true" v-if="editable">
                  <template v-slot:activator="{ on, attrs }">
                    <v-btn icon v-bind="attrs" v-on="on">
                      <v-icon>mdi-dots-vertical</v-icon>
                    </v-btn>
                  </template>
                  <v-list subheader>
                    <v-subheader>Targets</v-subheader>
                    <v-list-item v-for="(target, index) in targets" :key="index" @click.stop="toggleTarget(group, target)" >
                      <template v-slot:default="{ active }">
                        <v-list-item-icon>
                          <v-icon>{{ target in group.targets && group.targets[target].enabled ? 'mdi-checkbox-marked' : 'mdi-checkbox-blank-outline' }}</v-icon>
                        </v-list-item-icon>  
                        <v-list-item-content class="text-no-wrap">{{ capitalize(target) }} ({{ targetStatus(group.targets, target) }})</v-list-item-content>
                      </template>
                    </v-list-item>
                    <v-subheader>Actions</v-subheader>
                    <rename-group
                      :group="group"
                      @submitted="onRenameGroup"
                    ></rename-group>
                    <!-- <v-list-item @click.stop="onSyncGroup(group)" :disabled="!group.locked">
                      <v-list-item-icon>
                        <v-icon :disabled="!group.locked">mdi-sync</v-icon>
                      </v-list-item-icon>
                      <v-list-item-title>Sync Group</v-list-item-title>
                    </v-list-item> -->
                    <v-list-item @click.stop="onLockGroup(group)">
                      <v-list-item-icon>
                        <v-icon>{{ group.locked ? 'mdi-lock-open-variant-outline' : 'mdi-lock-outline' }}</v-icon>
                      </v-list-item-icon>
                      <v-list-item-title>{{ group.locked ? 'Unlock Group' : 'Lock Group' }}</v-list-item-title>
                    </v-list-item>
                    <v-list-item @click.stop="onExportGroup(group)">
                      <v-list-item-icon>
                        <v-icon>mdi-file-export-outline</v-icon>
                      </v-list-item-icon>
                      <v-list-item-title>Export to ComboCurve</v-list-item-title>
                    </v-list-item>
                    <v-list-item @click.stop="onExportAriesGroup(group)">
                      <v-list-item-icon>
                        <v-icon>mdi-file-export-outline</v-icon>
                      </v-list-item-icon>
                      <v-list-item-title>Export to ARIES</v-list-item-title>
                    </v-list-item>
                    <v-list-item @click.stop="onDeleteGroup(group)">
                      <v-list-item-icon>
                        <v-icon>mdi-trash-can-outline</v-icon>
                      </v-list-item-icon>
                      <v-list-item-title>Delete Group</v-list-item-title>
                    </v-list-item>
                    <group-info v-if="debug" :group="group"></group-info>
                  </v-list>
                </v-menu>
              </v-toolbar>
            </v-card>
          </v-item>
        </v-col>
      </v-row>
    </v-item-group>
    <div v-else>You have not created any groups yet!</div>
  </div>
</template>

<script>
import Predictions from "@/api/predictions"
import Exports from "@/api/exports"
import GroupInfo from "@/components/Apps/Forecast/GroupInfo"
import RenameGroup from "@/components/Apps/Forecast/Dialogs/RenameGroup"
import { deriveAriesQualifier } from "@/utils/aries"
export default {
  name: "AXGroups",
  props: {
    uuid: { type: String, required: true },
    // Only needed by onExportGroup(), reachable solely through the 3-dot
    // menu that :editable="false" already hides entirely (e.g.
    // Economics.vue's read-only usage) -- not required so callers that
    // never show that menu don't need to supply it.
    predictionId: { type: Number, default: null },
    groups: { type: Object, required: true },
    targets: { type: Array, required: true },
    editable: { type: Boolean, default: true },
    debug: { type: Boolean, default: true },
  },
  components: {
    GroupInfo, RenameGroup
  },
  data() {
    return {
      phases: [],
    }
  },
  computed: {
    selectedGroup() {
      if (this.isEmpty(this.groups.selected)) return undefined
      return this.groups.list.find(g => g.id == this.groups.selected)
    }
  },
  methods: {
    cardColor(group) {
      let color = []
      if (group.locked) {
        color.push('purple')
        if (this.groups.selected == group.id) {
          color.push(this.isDark ? 'darken-1' : 'darken-1')
        } else {
          color.push(this.isDark ? 'darken-4' : 'lighten-3')
        }
      } else {
        color.push('grey')
        if (this.groups.selected == group.id) {
          color.push(this.isDark ? 'darken-1' : 'darken-1')
        } else {
          color.push(this.isDark ? 'darken-4' : 'lighten-3')
        }
      }
      return color.join(' ')
    },
    badgeColor(target) {
      if (target == 'oil') return 'green'
      if (target == 'gas') return 'red'
      if (target == 'water') return 'light-blue'
    },
    targetStatus(targets, target) {
      // Check each target (oil, gas, water)
      if (target in targets) {
        if ('termination' in targets[target] && !this.isEmpty(targets[target]['termination'])) {
          return 'Terminated'
        }
        if ('curves' in targets[target] && !this.isEmpty(targets[target]['curves'])) {
          return 'Terminated'
        }
        // Then check params (second priority)
        else if ('params' in targets[target] && !this.isEmpty(targets[target]['params'])) {
          return 'Declined'
        }
        // Default to forecast if neither curves nor params have data
        else {
          return 'Forecasted'
        }
      }
      return 'Not Set'
    },
    toggleTarget(group, target) {
      if (!(target in group.targets)) {
        this.$set(group.targets, target, { enabled: true })
      }
      else {
        if ('enabled' in group.targets[target]) {
          group.targets[target].enabled = !group.targets[target].enabled
        } else {
          this.$set(group.targets[target], 'enabled', false)
        }
      }
    },  
    onRenameGroup(group) {
      this.groups.list.find(g => g.id == group.id).name = group.name
      this.saveGroups()
    },
    onLockGroup(group) {
      if (group.locked) {
        group.locked = !group.locked
        this.saveGroups()
        return
      } else {
        // Only validate if both oil AND gas targets exist and are enabled
        if ('oil' in group.targets && 'gas' in group.targets && 
            group.targets['oil'].enabled && group.targets['gas'].enabled) {
          let oil = this.targetStatus(group.targets, 'oil')
          let gas = this.targetStatus(group.targets, 'gas')
          if (
            (oil == 'Declined' && gas == 'Declined') ||
            (oil == 'Terminated' && gas == 'Terminated') ||
            (oil == 'Forecasted' && gas == 'Forecasted')
          ) {
            group.locked = !group.locked
            this.$emit('group-locked', group)
            this.saveGroups()
          } else {
            this.notify('Unable to lock mismatched targets in group.', 'error')
          }
        } else {
          // Single target - just lock it
          group.locked = !group.locked
          this.$emit('group-locked', group)
          this.saveGroups()
        }
      }
    },
    onExportGroup(group) {
      // No lock/sync needed, no form -- unlike Reports, this never reads
      // a group's own edited params/curves/termination overrides, just
      // its settings (already captured when the group was created) and
      // its filters. Wells are NOT read from group.wells here -- that
      // snapshot has proven unreliable, so the backend re-derives the
      // current matching wells from the group's own filters at
      // generation time instead (models/export.py's generate()), the
      // same call sync_groups() makes. Creates the export right here with
      // the rest as its starting config (still freely editable
      // afterward, same as any export), then heads to the Exports list --
      // same landing spot Exports/Create.vue's own submit uses.
      let enabledTargets = Object.entries(group.targets || {})
        .filter(([, target]) => target.enabled)
        .map(([target]) => target)
      let payload = {
        prediction: this.predictionId,
        config: {
          format: 'combocurve',
          group: group.id,
          targets: enabledTargets,
          methods: [group.settings.method],
          technique: group.settings.technique,
          well_id_format: 'API 10',
          terminate: false,
          strategy: null,
          limit: null,
        },
        owner: this.user.id,
      }
      Exports.create(payload).then(_ => {
        this.notify(`Generating ComboCurve export for "${group.name}"`)
        this.$router.push({ name: 'exportList' })
      })
    },
    onExportAriesGroup(group) {
      // Same one-click shape as onExportGroup() above, with the group's
      // own single method/technique (more models require the standalone
      // Exporter tab). Water excluded -- causes import issues in ARIES.
      let enabledTargets = Object.entries(group.targets || {})
        .filter(([target, t]) => t.enabled && target != 'water')
        .map(([target]) => target)
      let payload = {
        prediction: this.predictionId,
        config: {
          format: 'aries',
          group: group.id,
          targets: enabledTargets,
          models: [{
            method: group.settings.method,
            technique: group.settings.technique,
            qualifier: deriveAriesQualifier(group.settings.method, group.settings.technique),
          }],
          well_id_format: 'API 10',
          terminate: false,
          strategy: null,
          limit: null,
        },
        owner: this.user.id,
      }
      Exports.create(payload).then(_ => {
        this.notify(`Generating ARIES export for "${group.name}"`)
        this.$router.push({ name: 'exportList' })
      })
    },
    onDeleteGroup(group) {
      this.groups.selected = undefined
      this.groups.list = this.groups.list.filter((g) => g.id !== group.id)
      this.saveGroups()
    },
    saveGroups() {
      let payload = { 'groups': JSON.parse(JSON.stringify(this.groups.list)) }
      Predictions.patch(this.uuid, payload)
    },
  }
}
</script>